import { nil, UIColor, UINativeScrollView, UITextView, UIView, YES } from "uicore-ts"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHNotificationBadge } from "../../Custom components/RH/RHNotificationBadge"
import { formatRHTime } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHPrivateMessageThreadViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHProcurementPrivateMessageThread } from "../../SocketClientFunctions"


class PrivateMessageThreadRowView extends UIView {

    readonly offererCompanyId: string

    readonly selectionButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "" },
            variant: "link",
            contentPadding: 0
        })
        .addedAsSubviewToView(this)

    readonly companyNameLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary,
            textAlignment: UITextView.textAlignment.left,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightBold,
                lineHeight: RHFont.lineHeightBase,
                overflow: "hidden",
                pointerEvents: "none",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }
        })
        .addedAsSubviewToView(this)

    readonly lastMessageTimeLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textTimestamp,
            textAlignment: UITextView.textAlignment.right,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase,
                pointerEvents: "none",
                whiteSpace: "nowrap"
            }
        })
        .addedAsSubviewToView(this)

    readonly lastMessagePreviewLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textSecondary,
            textAlignment: UITextView.textAlignment.left,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: RHFont.lineHeightBase,
                overflow: "hidden",
                pointerEvents: "none",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
            }
        })
        .addedAsSubviewToView(this)

    constructor(thread: RHProcurementPrivateMessageThread) {
        super()

        this.offererCompanyId = thread.offererCompanyId

        ;(this as PrivateMessageThreadRowView).configureWithObject({
            backgroundColor: RHColors.white,
            style: {
                border: `${RHPrivateMessageThreadViewTokens.threadListRowBorderWidth}px solid ${RHColors.slateDark.stringValue}`,
                borderRadius: RHButtons.borderRadius,
                boxShadow: "none",
                boxSizing: "border-box",
                overflow: "hidden"
            }
        })

        this.companyNameLabel.text = thread.offererCompanyName
        this.selectionButton.configureWithObject({
            hoverText: thread.offererCompanyName
        })

        let previewText = "Vestlust pole veel alustatud."
        let lastMessageTime = ""
        const lastMessage = thread.messages[thread.messages.length - 1]
        if (lastMessage) {
            previewText = lastMessage.bodyText.replace(/\s+/g, " ").trim()
            if (!previewText) {
                previewText = "Lisatud fail"
            }
            lastMessageTime = formatRHTime(new Date(lastMessage.createdAt))
        }

        this.lastMessagePreviewLabel.text = previewText
        this.lastMessageTimeLabel.text = lastMessageTime
        RHNotificationBadge.applyDot(
            this.companyNameLabel,
            thread.hasUnreadForCurrentViewer,
            "Lugemata privaatne sõnum."
        )
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        bounds.assignedAsFrameOfView(this.selectionButton)

        const contentFrame = bounds.rectangleWithInsets(
            RHPrivateMessageThreadViewTokens.threadListRowHorizontalPadding,
            RHPrivateMessageThreadViewTokens.threadListRowHorizontalPadding,
            RHSpacing.small,
            RHSpacing.small
        )
        const textRowHeight = RHPrivateMessageThreadViewTokens.threadListRowTextHeight
        const metadataFrame = contentFrame.rectangleWithHeight(textRowHeight)
        const timestampWidth = Math.min(
            contentFrame.width,
            this.lastMessageTimeLabel.intrinsicContentWidth(textRowHeight) + RHSpacing.small
        )

        metadataFrame.distributeViewsAlongWidth(
            [this.companyNameLabel, this.lastMessageTimeLabel],
            undefined,
            RHSpacing.small,
            [nil, timestampWidth]
        )
        metadataFrame
            .rectangleForNextRow(RHSpacing.tiny, textRowHeight)
            .assignedAsFrameOfView(this.lastMessagePreviewLabel)
    }

}


/**
 * Left-pane list of the procurer's private threads, one row per offerer
 * company, plus the "Saada mitmele" bulk-send action above them — named to
 * stay distinct from clicking directly into a row, which is now the direct
 * path for messaging a single company. Each row keeps its RHButton interaction
 * surface separate from the white card presentation owned by the row view.
 */
export class RHPrivateMessageThreadListView extends UINativeScrollView {

    readonly startConversationButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "+ Saada mitmele" },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding,
            style: { fontSize: RHButtons.compactFontSize },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.startConversationWasPressed?.()
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly emptyStateLabel = new UITextView()
        .configuredWithObject({
            text: "Vestlusi pole veel alustatud.",
            style: {
                fontSize: "13px",
                fontStyle: "italic",
                textAlign: "center",
                padding: `${RHSpacing.medium}px`
            }
        })

    threadDidSelect?: (offererCompanyId: string) => void
    startConversationWasPressed?: () => void

    _rowButtons: RHButton[] = []
    _rowViews: PrivateMessageThreadRowView[] = []
    _threads: RHProcurementPrivateMessageThread[] = []
    _selectedOffererCompanyId?: string


    configureWithThreads(threads: RHProcurementPrivateMessageThread[], selectedCompanyId?: string) {
        this._threads = threads
        this._selectedOffererCompanyId = threads[0]?.offererCompanyId
        if (
            selectedCompanyId &&
            threads.some(thread => thread.offererCompanyId === selectedCompanyId)
        ) {
            this._selectedOffererCompanyId = selectedCompanyId
        }

        this._rowViews.everyElement.removeFromSuperview()
        this._rowButtons = []
        this._rowViews = []

        if (threads.length === 0) {
            this.emptyStateLabel.addedAsSubviewToView(this)
            this.setNeedsLayout()
            return
        }
        this.emptyStateLabel.removeFromSuperview()

        const applySelectionPresentation = () => {
            for (const rowView of this._rowViews) {
                rowView.backgroundColor = RHColors.white
                rowView.style.borderWidth = `${RHPrivateMessageThreadViewTokens.threadListRowBorderWidth}px`
                if (rowView.offererCompanyId === this._selectedOffererCompanyId) {
                    rowView.backgroundColor = RHColors.primaryBlueSurface
                    rowView.style.borderWidth = `${RHPrivateMessageThreadViewTokens.threadListSelectedRowBorderWidth}px`
                }
            }
        }

        for (const thread of threads) {
            const rowView = new PrivateMessageThreadRowView(thread)
                .addedAsSubviewToView(this)
            const rowButton = rowView.selectionButton
            rowButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                this._selectedOffererCompanyId = thread.offererCompanyId
                applySelectionPresentation()
                this.threadDidSelect?.(thread.offererCompanyId)
            }
            this._rowButtons.push(rowButton)
            this._rowViews.push(rowView)
        }

        applySelectionPresentation()

        this.setNeedsLayout()
    }


    get selectedOffererCompanyId(): string | undefined {
        return this._selectedOffererCompanyId
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        const rowHeight = RHPrivateMessageThreadViewTokens.threadListRowHeight

        let current = bounds
            .rectangleWithHeight(RHButtons.compactHeight)
            .assignedAsFrameOfView(this.startConversationButton)

        if (this._threads.length === 0) {
            current.rectangleForNextRow(RHSpacing.small, this.emptyStateLabel)
                .assignedAsFrameOfView(this.emptyStateLabel)
            return
        }

        for (const rowView of this._rowViews) {
            current = current.rectangleForNextRow(RHPrivateMessageThreadViewTokens.threadListRowGap, rowHeight)
                .assignedAsFrameOfView(rowView)
        }
    }


    override intrinsicContentHeight(constrainingWidth?: number): number {
        // Fills a fixed height assigned by the parent split view.
        return 0
    }

}
